import { useEffect, useRef, useState } from "react";

/**
 * Fake TikTok Video Generator (pure JSX)
 * - Upload base video (MP4/MOV/WebM)
 * - Customise overlay (username, caption, counts, music, theme, toggles, positions)
 * - Renders to 1080x1920 canvas; records to WebM via MediaRecorder
 * - Everything runs in-browser; no uploads
 */

export default function TikTokGenerator() {
  // Canvas + media refs
  const canvasRef = useRef(null);
  const videoRef = useRef(null);

  // Render size (export)
  const WIDTH = 1080;
  const HEIGHT = 1920;

  // Inputs / state
  const [videoFile, setVideoFile] = useState(null);
  const [videoURL, setVideoURL] = useState(null);

  const [profileImgFile, setProfileImgFile] = useState(null);
  const [profileImgBitmap, setProfileImgBitmap] = useState(null);

  const [username, setUsername] = useState("@user.name");
  const [verified, setVerified] = useState(true);
  const [caption, setCaption] = useState(
    "Here’s a totally normal caption with #hashtags and a sprinkle of ✨"
  );
  const [musicTitle, setMusicTitle] = useState("original sound - username");

  const [likes, setLikes] = useState(12800);
  const [comments, setComments] = useState(356);
  const [shares, setShares] = useState(42);

  const [showStatusBar, setShowStatusBar] = useState(true);
  const [showSidebar, setShowSidebar] = useState(true);
  const [showProgressBar, setShowProgressBar] = useState(true);
  const [showMusicTicker, setShowMusicTicker] = useState(true);
  const [showWatermark, setShowWatermark] = useState(true);
  const [followButton, setFollowButton] = useState(true);

  // Positions (percent of WIDTH/HEIGHT)
  const [leftBlockX, setLeftBlockX] = useState(50);
  const [leftBlockY, setLeftBlockY] = useState(62);
  const [rightBlockX, setRightBlockX] = useState(92);
  const [rightBlockY, setRightBlockY] = useState(52);
  const [bottomBlockX, setBottomBlockX] = useState(50);
  const [bottomBlockY, setBottomBlockY] = useState(93);

  // Theme
  const [theme, setTheme] = useState("dark");
  const fg = theme === "dark" ? "#ffffff" : "#111111";
  const bg = theme === "dark" ? "#000000" : "#ffffff";

  // Recording
  const [recording, setRecording] = useState(false);
  const recordingRef = useRef(false); // avoids stale closure
  const [recordProgress, setRecordProgress] = useState(0);
  const chunksRef = useRef([]);
  const mediaRecorderRef = useRef(null);
  const [outputURL, setOutputURL] = useState(null);

  // --- Utils ---
  function formatCount(n) {
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1) + "M";
    if (n >= 1_000) return (n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1) + "K";
    return String(n);
  }

  // --- Upload handling ---
  useEffect(() => {
    if (!videoFile) return;
    const url = URL.createObjectURL(videoFile);
    setVideoURL(url);
    return () => URL.revokeObjectURL(url);
  }, [videoFile]);

  useEffect(() => {
    let revoked = false;
    if (!profileImgFile) {
      setProfileImgBitmap(null);
      return;
    }
    const url = URL.createObjectURL(profileImgFile);
    (async () => {
      try {
        const resp = await fetch(url);
        const blob = await resp.blob();
        const bmp = await createImageBitmap(blob);
        if (!revoked) setProfileImgBitmap(bmp);
      } catch (e) {
        console.error(e);
      } finally {
        URL.revokeObjectURL(url);
      }
    })();
    return () => {
      revoked = true;
    };
  }, [profileImgFile]);

  // --- Drawing helpers ---
  function setShadow(ctx, on = true) {
    if (on) {
      ctx.shadowColor = theme === "dark" ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.2)";
      ctx.shadowBlur = 10;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 2;
    } else {
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
    }
  }

  function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
    const words = String(text).split(/\s+/);
    let line = "";
    let sy = y;
    for (let n = 0; n < words.length; n++) {
      const testLine = line + words[n] + " ";
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && n > 0) {
        ctx.fillText(line, x, sy);
        line = words[n] + " ";
        sy += lineHeight;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, x, sy);
    return sy; // return last baseline
  }

  function drawRoundedRect(ctx, x, y, w, h, r) {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
  }

  // Simple vector icons
  function drawHeart(ctx, x, y, s) {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(s, s);
    ctx.beginPath();
    ctx.moveTo(0, 6);
    ctx.bezierCurveTo(0, 2.5, 2.5, 0, 5.5, 0);
    ctx.bezierCurveTo(7.5, 0, 9.5, 1, 10.5, 2.5);
    ctx.bezierCurveTo(11.5, 1, 13.5, 0, 15.5, 0);
    ctx.bezierCurveTo(18.5, 0, 21, 2.5, 21, 6);
    ctx.bezierCurveTo(21, 12, 14, 15.5, 10.5, 20);
    ctx.bezierCurveTo(7, 15.5, 0, 12, 0, 6);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
  function drawComment(ctx, x, y, s) {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(s, s);
    drawRoundedRect(ctx, 0, 0, 22, 16, 4);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(8, 16);
    ctx.lineTo(10, 20);
    ctx.lineTo(14, 16);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
  function drawShare(ctx, x, y, s) {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(s, s);
    ctx.beginPath();
    ctx.moveTo(0, 10);
    ctx.lineTo(14, 10);
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(10, 6);
    ctx.lineTo(14, 10);
    ctx.lineTo(10, 14);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
  function drawMusicNote(ctx, x, y, s) {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(s, s);
    ctx.beginPath(); ctx.rect(6, 0, 2, 14); ctx.fill();
    ctx.beginPath(); ctx.arc(5, 14, 4, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.rect(8, 0, 8, 3); ctx.fill();
    ctx.beginPath(); ctx.arc(18, 14, 4, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }
  function drawVerified(ctx, x, y) {
    ctx.save();
    ctx.translate(x, y);
    ctx.beginPath(); ctx.arc(0, 0, 14, 0, Math.PI * 2); ctx.fillStyle = "#2ea6ff"; ctx.fill();
    ctx.lineWidth = 3; ctx.strokeStyle = "white";
    ctx.beginPath(); ctx.moveTo(-6, 0); ctx.lineTo(-1, 6); ctx.lineTo(8, -6); ctx.stroke();
    ctx.restore();
  }

  function drawStatusBar(ctx) {
    if (!showStatusBar) return;
    const time = new Date();
    const hh = String(time.getHours()).padStart(2, "0");
    const mm = String(time.getMinutes()).padStart(2, "0");

    ctx.save();
    ctx.font = "36px system-ui, -apple-system, Segoe UI, Roboto";
    ctx.fillStyle = fg;
    setShadow(ctx, true);
    ctx.fillText(`${hh}:${mm}`, 48, 72);

    setShadow(ctx, false);
    ctx.strokeStyle = fg;
    ctx.lineWidth = 3;
    // WiFi arcs
    ctx.beginPath(); ctx.arc(WIDTH - 200, 60, 16, Math.PI, 2 * Math.PI); ctx.stroke();
    ctx.beginPath(); ctx.arc(WIDTH - 200, 60, 10, Math.PI, 2 * Math.PI); ctx.stroke();
    // Battery
    ctx.strokeRect(WIDTH - 120, 44, 60, 28);
    ctx.fillStyle = fg;
    ctx.fillRect(WIDTH - 58, 50, 34, 16);
    ctx.fillRect(WIDTH - 56, 50, 2, 16);
    ctx.restore();
  }

  function drawWatermark(ctx, currentTime) {
    if (!showWatermark) return;
    ctx.save();
    ctx.globalAlpha = 0.65;
    ctx.font = "bold 38px system-ui, -apple-system, Segoe UI";
    ctx.fillStyle = theme === "dark" ? "#ffffff" : "#111111";
    const y1 = 1400 + Math.sin(currentTime * 2) * 6;
    ctx.fillText("TikTok", 28, y1);
    ctx.fillText(username, 28, y1 + 52);
    ctx.restore();
  }

  function drawProfileCircle(ctx, img, cx, cy, r) {
    ctx.save();
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.closePath(); ctx.clip();
    if (img) ctx.drawImage(img, cx - r, cy - r, 2 * r, 2 * r);
    else { ctx.fillStyle = "#999"; ctx.fillRect(cx - r, cy - r, 2 * r, 2 * r); }
    ctx.restore();

    if (followButton) {
      // small plus under avatar
      ctx.save();
      ctx.fillStyle = "#ff3b5c";
      ctx.beginPath(); ctx.arc(cx, cy + r + 24, 18, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "white";
      ctx.fillRect(cx - 2, cy + r + 16, 4, 16);
      ctx.fillRect(cx - 10, cy + r + 22, 20, 4);
      ctx.restore();
    }
  }

  function drawSidebarBlock(ctx, x, y, tcol) {
    if (!showSidebar) return;
    setShadow(ctx, true);
    ctx.fillStyle = tcol;

    // Profile
    drawProfileCircle(ctx, profileImgBitmap, x, y, 40);

    // Heart
    ctx.save(); ctx.translate(x - 20, y + 120); drawHeart(ctx, 0, 0, 2.0); ctx.restore();
    ctx.font = "bold 36px system-ui, -apple-system, Segoe UI";
    ctx.textAlign = "center";
    ctx.fillText(formatCount(likes), x, y + 200);

    // Comment
    ctx.save(); ctx.translate(x - 20, y + 260); drawComment(ctx, 0, 0, 2.0); ctx.restore();
    ctx.fillText(formatCount(comments), x, y + 340);

    // Share
    ctx.save(); ctx.translate(x - 14, y + 400); drawShare(ctx, 0, 0, 2.0); ctx.restore();
    ctx.fillText(formatCount(shares), x, y + 480);
  }

  function drawLeftMeta(ctx, xPct, yPct, tcol) {
    const left = (xPct / 100) * WIDTH;
    const top = (yPct / 100) * HEIGHT;

    setShadow(ctx, true);
    ctx.textAlign = "left";
    ctx.fillStyle = tcol;

    // Username
    ctx.font = "bold 44px system-ui, -apple-system, Segoe UI";
    ctx.fillText(username, left, top);
    if (verified) drawVerified(ctx, left + ctx.measureText(username).width + 26, top - 16);

    // Caption
    ctx.font = "32px system-ui, -apple-system, Segoe UI";
    const lastBaseline = wrapText(ctx, caption, left, top + 46, WIDTH * 0.6, 44);

    // Music ticker
    if (showMusicTicker) {
      ctx.save();
      ctx.globalAlpha = 0.92;
      ctx.font = "bold 30px system-ui, -apple-system, Segoe UI";
      drawMusicNote(ctx, left, lastBaseline + 24, 1.2);
      ctx.fillText(musicTitle, left + 46, lastBaseline + 38);
      ctx.restore();
    }
  }

  function drawBottomProgress(ctx, xPct, yPct, progress, tcol) {
    if (!showProgressBar) return;
    const cx = (xPct / 100) * WIDTH;
    const cy = (yPct / 100) * HEIGHT;
    const w = WIDTH * 0.86;
    const h = 8;
    const x = cx - w / 2;
    const y = cy - h / 2;

    ctx.save();
    ctx.globalAlpha = 0.75;
    ctx.fillStyle = theme === "dark" ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)";
    drawRoundedRect(ctx, x, y, w, h, 4); ctx.fill();

    ctx.fillStyle = theme === "dark" ? "#fff" : "#000";
    const pw = Math.max(12, w * progress);
    drawRoundedRect(ctx, x, y, pw, h, 4); ctx.fill();
    ctx.restore();
  }

  function drawTopTabs(ctx) {
    // Fake "Following | For You"
    ctx.save();
    setShadow(ctx, true);
    ctx.font = "bold 38px system-ui, -apple-system, Segoe UI";
    ctx.textAlign = "center";
    ctx.fillStyle = fg;
    ctx.fillText("Following    |    For You", WIDTH / 2, 120);
    ctx.restore();
  }

  function drawFrame(ctx) {
    const canvas = canvasRef.current;
    const video = videoRef.current;

    // BG
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Video fitted (contain)
    if (video && video.readyState >= 2) {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (vw && vh) {
        const scale = Math.min(WIDTH / vw, HEIGHT / vh);
        const dw = vw * scale;
        const dh = vh * scale;
        const dx = (WIDTH - dw) / 2;
        const dy = (HEIGHT - dh) / 2;
        ctx.drawImage(video, dx, dy, dw, dh);
      }
    }

    // Overlays
    drawStatusBar(ctx);
    drawTopTabs(ctx);
    drawLeftMeta(ctx, leftBlockX, leftBlockY, fg);
    drawSidebarBlock(ctx, (rightBlockX / 100) * WIDTH, (rightBlockY / 100) * HEIGHT, fg);

    const progress = video && video.duration ? video.currentTime / video.duration : 0;
    drawBottomProgress(ctx, bottomBlockX, bottomBlockY, progress, fg);
    drawWatermark(ctx, (video && video.currentTime) || 0);
  }

  // Animation loop
  const rafRef = useRef(null);
  const drawLoop = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    const ctx = canvas.getContext("2d");
    drawFrame(ctx);
    rafRef.current = requestAnimationFrame(drawLoop);
  };

  useEffect(() => {
    rafRef.current = requestAnimationFrame(drawLoop);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    videoURL, theme, username, caption, musicTitle,
    likes, comments, shares, verified,
    showStatusBar, showSidebar, showProgressBar, showMusicTicker, showWatermark, followButton,
    leftBlockX, leftBlockY, rightBlockX, rightBlockY, bottomBlockX, bottomBlockY
  ]);

  // High-DPI canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(WIDTH * dpr);
    canvas.height = Math.floor(HEIGHT * dpr);
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key.toLowerCase();
      if (k === "r") startRecording();
      if (k === "s") savePreviewFramePNG();
      if (k === "p") {
        const v = videoRef.current;
        if (v) v.paused ? v.play() : v.pause();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  async function getCanvasStreamWithAudio(canvas, video) {
    // 30 fps canvas stream
    const canvasStream = canvas.captureStream(30);
  
    // Try to grab audio directly from the <video> element
    let audioTracks = [];
    try {
      if (video.captureStream) {
        const vStream = video.captureStream();
        audioTracks = vStream.getAudioTracks();
      }
    } catch (_) {}
  
    // Fallback: Web Audio route
    if (audioTracks.length === 0) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ac = new AudioCtx();
      await ac.resume(); // required on Safari/iOS after a user gesture
  
      // Only create ONE source per media element in your app’s lifetime
      if (!video._mediaElementSource) {
        video._mediaElementSource = ac.createMediaElementSource(video);
        video._mediaElementSource.connect(ac.destination); // so you can hear it while recording
      }
      const dest = ac.createMediaStreamDestination();
      video._mediaElementSource.connect(dest);
      audioTracks = dest.stream.getAudioTracks();
    }
  
    // Merge audio into the canvas stream
    audioTracks.forEach((t) => canvasStream.addTrack(t));
    return canvasStream;
  }

  // --- Recording ---
async function startRecording() {
  const canvas = canvasRef.current;
  const video = videoRef.current;
  if (!canvas || !video) return;
  if (!videoURL) {
    alert("Upload a video first.");
    return;
  }

  setRecordProgress(0);
  recordingRef.current = true;
  setRecording(true);

  // Ensure audio is audible to the capture graph
  video.muted = false;
  video.volume = 1.0;

  // Canvas (video) + source audio
  const stream = await getCanvasStreamWithAudio(canvas, video);

  // MIME selection (broad support)
  const candidates = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
    "video/mp4;codecs=h264",
    "video/mp4"
  ];
  function pickMime() {
    if (!window.MediaRecorder || !MediaRecorder.isTypeSupported) return null;
    for (const m of candidates) {
      try { if (MediaRecorder.isTypeSupported(m)) return m; } catch {}
    }
    return null;
  }
  const chosen = pickMime();
  let mr;
  try {
    mr = chosen ? new MediaRecorder(stream, { mimeType: chosen }) : new MediaRecorder(stream);
  } catch (e) {
    setRecording(false);
    recordingRef.current = false;
    alert(`Recording not available.\n\n${e}`);
    return;
  }
  mediaRecorderRef.current = mr;
  chunksRef.current = [];

  mr.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
  mr.onstop = () => {
    const type = chosen || (chunksRef.current[0] && chunksRef.current[0].type) || "video/webm";
    const blob = new Blob(chunksRef.current, { type });
    const url = URL.createObjectURL(blob);
    setOutputURL((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    setRecording(false);
    recordingRef.current = false;

    // Re-mute after export to keep silent preview behavior
    video.muted = true;
  };

  // Start playback & recording
  video.currentTime = 0;
  await video.play().catch(() => {});
  try { mr.start(); } catch (e) {
    setRecording(false);
    recordingRef.current = false;
    alert(`Couldn’t start recording: ${e}`);
    return;
  }

  // Progress + auto-stop
  const duration = isFinite(video.duration) ? video.duration : 0;
  const tick = () => {
    if (!recordingRef.current) return;
    const t = video.currentTime;
    if (duration > 0) setRecordProgress(Math.min(1, t / duration));
    if (video.ended || (duration > 0 && t >= duration)) {
      try { mr.stop(); } catch {}
      video.pause();
      setRecordProgress(1);
    } else {
      requestAnimationFrame(tick);
    }
  };
  requestAnimationFrame(tick);
}

  function stopRecording() {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state === "recording") mr.stop();
    recordingRef.current = false;
    setRecording(false);
    const v = videoRef.current;
    if (v && !v.paused) v.pause();
  }

  function downloadOutput() {
    if (!outputURL) return;
    const a = document.createElement("a");
    a.href = outputURL;
    a.download = "fake-tiktok.webm";
    a.click();
  }

  function savePreviewFramePNG() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = "frame.png";
    a.click();
  }

  // --- UI ---
  const styles = {
    page: { minHeight: "100vh", background: "linear-gradient(180deg,#111,#000)", color: "#fff", padding: 16 },
    container: { margin: "0 auto", maxWidth: 1400, display: "flex", gap: 20, flexWrap: "wrap" },
    phone: { width: 360, height: 640, borderRadius: 16, overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 10px 40px rgba(0,0,0,0.6)", position: "relative" },
    controls: { flex: "0 0 420px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: 16 },
    label: { fontSize: 12, opacity: 0.85, display: "block", marginTop: 8 },
    input: { width: "100%", background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", borderRadius: 10, padding: "10px 12px", marginTop: 6, boxSizing: "border-box" },
    row: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
    row3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 },
    btnRow: { display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" },
    btn: (bg) => ({ background: bg, border: "none", color: "#fff", borderRadius: 12, padding: "10px 14px", cursor: "pointer", fontWeight: 600 }),
    small: { fontSize: 12, opacity: 0.75, marginTop: 8 },
    progressWrap: { width: "100%", background: "rgba(255,255,255,0.1)", borderRadius: 10, overflow: "hidden", marginTop: 8 },
    progressBar: (p) => ({ height: 6, width: `${Math.round(p * 100)}%`, background: "#fff" }),
    hint: { fontSize: 11, opacity: 0.6, marginTop: 6 }
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Preview */}
        <div style={{ flex: "1 1 360px", display: "grid", placeItems: "center" }}>
          <div style={styles.phone}>
            <canvas ref={canvasRef} style={{ width: 360, height: 640, display: "block" }} />
            {/* Hidden video source */}
            <video
              ref={videoRef}
              src={videoURL || undefined}
              playsInline
              muted
              style={{ display: "none" }}
              onLoadedMetadata={() => videoRef.current && videoRef.current.play().catch(() => {})}
            />
          </div>
          <div style={styles.hint}>Preview scales 1080×1920 down to 360×640. Shortcuts: R=Record, P=Play/Pause, S=Save PNG.</div>
        </div>

        {/* Controls */}
        <div style={styles.controls}>
          <h2 style={{ marginTop: 0 }}>Overlay Controls</h2>

          <label style={styles.label}>
            Base video
            <input type="file" accept="video/*" style={styles.input}
              onChange={(e) => setVideoFile(e.target.files?.[0] || null)} />
          </label>

          <label style={styles.label}>
            Profile picture (optional)
            <input type="file" accept="image/*" style={styles.input}
              onChange={(e) => setProfileImgFile(e.target.files?.[0] || null)} />
          </label>

          <div style={styles.row}>
            <label style={styles.label}>
              Theme
              <select style={styles.input} value={theme} onChange={(e) => setTheme(e.target.value)}>
                <option value="dark">Dark</option>
                <option value="light">Light</option>
              </select>
            </label>
            <label style={styles.label}>
              Verified
              <input type="checkbox" checked={verified} style={{ marginLeft: 8, verticalAlign: "middle" }}
                onChange={(e) => setVerified(e.target.checked)} />
            </label>
          </div>

          <label style={styles.label}>
            Username
            <input style={styles.input} value={username} onChange={(e) => setUsername(e.target.value)} />
          </label>

          <label style={styles.label}>
            Caption
            <textarea rows={3} style={styles.input} value={caption} onChange={(e) => setCaption(e.target.value)} />
          </label>

          <label style={styles.label}>
            Music title
            <input style={styles.input} value={musicTitle} onChange={(e) => setMusicTitle(e.target.value)} />
          </label>

          <div style={styles.row3}>
            <label style={styles.label}>
              Likes
              <input type="number" style={styles.input} value={likes} onChange={(e) => setLikes(parseInt(e.target.value || "0", 10))} />
            </label>
            <label style={styles.label}>
              Comments
              <input type="number" style={styles.input} value={comments} onChange={(e) => setComments(parseInt(e.target.value || "0", 10))} />
            </label>
            <label style={styles.label}>
              Shares
              <input type="number" style={styles.input} value={shares} onChange={(e) => setShares(parseInt(e.target.value || "0", 10))} />
            </label>
          </div>

          <div style={styles.row}>
            <label style={styles.label}>
              Show status bar
              <input type="checkbox" checked={showStatusBar} style={{ marginLeft: 8, verticalAlign: "middle" }}
                onChange={(e) => setShowStatusBar(e.target.checked)} />
            </label>
            <label style={styles.label}>
              Show sidebar
              <input type="checkbox" checked={showSidebar} style={{ marginLeft: 8, verticalAlign: "middle" }}
                onChange={(e) => setShowSidebar(e.target.checked)} />
            </label>
            <label style={styles.label}>
              Show progress
              <input type="checkbox" checked={showProgressBar} style={{ marginLeft: 8, verticalAlign: "middle" }}
                onChange={(e) => setShowProgressBar(e.target.checked)} />
            </label>
            <label style={styles.label}>
              Music ticker
              <input type="checkbox" checked={showMusicTicker} style={{ marginLeft: 8, verticalAlign: "middle" }}
                onChange={(e) => setShowMusicTicker(e.target.checked)} />
            </label>
            <label style={styles.label}>
              Watermark
              <input type="checkbox" checked={showWatermark} style={{ marginLeft: 8, verticalAlign: "middle" }}
                onChange={(e) => setShowWatermark(e.target.checked)} />
            </label>
            <label style={styles.label}>
              Follow button
              <input type="checkbox" checked={followButton} style={{ marginLeft: 8, verticalAlign: "middle" }}
                onChange={(e) => setFollowButton(e.target.checked)} />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Left block X/Y (%)</div>
              <input type="range" min={0} max={100} value={leftBlockX} onChange={(e) => setLeftBlockX(parseInt(e.target.value, 10))} style={{ width: "100%" }} />
              <input type="range" min={0} max={100} value={leftBlockY} onChange={(e) => setLeftBlockY(parseInt(e.target.value, 10))} style={{ width: "100%" }} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Right block X/Y (%)</div>
              <input type="range" min={0} max={100} value={rightBlockX} onChange={(e) => setRightBlockX(parseInt(e.target.value, 10))} style={{ width: "100%" }} />
              <input type="range" min={0} max={100} value={rightBlockY} onChange={(e) => setRightBlockY(parseInt(e.target.value, 10))} style={{ width: "100%" }} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Bottom bar X/Y (%)</div>
              <input type="range" min={0} max={100} value={bottomBlockX} onChange={(e) => setBottomBlockX(parseInt(e.target.value, 10))} style={{ width: "100%" }} />
              <input type="range" min={0} max={100} value={bottomBlockY} onChange={(e) => setBottomBlockY(parseInt(e.target.value, 10))} style={{ width: "100%" }} />
            </div>
          </div>

          <div style={styles.btnRow}>
            {!recording ? (
              <button onClick={startRecording} style={styles.btn("#16a34a")} disabled={!videoURL} title="Shortcut: R">
                ▶️ Record & Export
              </button>
            ) : (
              <button onClick={stopRecording} style={styles.btn("#dc2626")}>⏹ Stop</button>
            )}
            <button onClick={savePreviewFramePNG} style={styles.btn("rgba(255,255,255,0.12)")} title="Shortcut: S">
              Save PNG frame
            </button>
            <button onClick={downloadOutput} disabled={!outputURL} style={styles.btn(outputURL ? "#2563eb" : "rgba(255,255,255,0.12)")}>
              Download WebM
            </button>
          </div>

          {recording && (
            <div style={styles.progressWrap}>
              <div style={styles.progressBar(recordProgress)} />
            </div>
          )}

          {outputURL && (
            <div style={styles.small}>
              Exported! Click <span style={{ textDecoration: "underline", cursor: "pointer" }} onClick={downloadOutput}>Download WebM</span>.
              Need MP4? Convert locally with ffmpeg:
              <code style={{ background: "rgba(0,0,0,0.4)", padding: "2px 6px", borderRadius: 6, marginLeft: 6 }}>
                ffmpeg -i fake-tiktok.webm -c:v libx264 -crf 18 -preset veryfast -pix_fmt yuv420p out.mp4
              </code>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
